import React, { useEffect, useState } from 'react';
import { Check, FileText, Leaf, Apple, ClipboardList, Star, Clock, AlertTriangle, Heart } from 'lucide-react';
import { Section, CTAButton } from './Shared';
import { AcupunctureNeedle, MoxaStick, CuppingGlass } from './TCMIcons';
import { Pricing } from './Pricing';
const LaunchUrgency = () => {
  const [timeLeft, setTimeLeft] = useState({
    h: 23,
    m: 8,
    s: 46
  });
  const [spotsTaken, setSpotsTaken] = useState(() => {
    const saved = typeof window !== 'undefined' ? sessionStorage.getItem('mtc-launch-spots') : null;
    if (saved) {
      const {
        value,
        timestamp
      } = JSON.parse(saved);
      const elapsed = (Date.now() - timestamp) / 1000;
      const increment = Math.floor(elapsed / 60);
      return Math.min(value + increment, 92);
    }
    return 68;
  });
  useEffect(() => {
    sessionStorage.setItem('mtc-launch-spots', JSON.stringify({
      value: spotsTaken,
      timestamp: Date.now()
    }));
  }, [spotsTaken]);
  useEffect(() => {
    const id = setInterval(() => {
      setTimeLeft(prev => {
        let {
          h,
          m,
          s
        } = prev;
        if (s > 0) s--;else {
          s = 59;
          if (m > 0) m--;else {
            m = 59;
            if (h > 0) h--;
          }
        }
        return {
          h,
          m,
          s
        };
      });
    }, 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    const id = setInterval(() => {
      setSpotsTaken(prev => {
        if (prev >= 92) return prev;
        return Math.random() < 0.4 ? prev + 1 : prev;
      });
    }, 45000 + Math.random() * 30000);
    return () => clearInterval(id);
  }, []);
  const fmt = (n: number) => n.toString().padStart(2, '0');
  const TimeBox = ({
    value,
    label
  }: {
    value: number;
    label: string;
  }) => <div className="flex flex-col items-center" data-id="element-218">
      <div className="bg-brand-green text-white font-mono font-bold text-2xl md:text-4xl rounded-xl px-3 md:px-5 py-2 md:py-3 min-w-[56px] md:min-w-[80px] text-center shadow-md tabular-nums border border-brand-celesteLight/40" data-id="element-219">
        {fmt(value)}
      </div>
      <span className="text-[10px] md:text-xs font-medium text-brand-text/70 mt-1.5 uppercase tracking-wider" data-id="element-220">
        {label}
      </span>
    </div>;
  return <div className="max-w-4xl mx-auto mb-12" data-id="element-221">
      <div className="glass-panel rounded-3xl p-6 md:p-8 border-2 border-brand-celeste/40 bg-gradient-to-br from-white/95 to-brand-celesteLight/10 shadow-[0_10px_40px_rgba(14,165,233,0.12)]" data-id="element-222">
        <div className="flex items-center justify-center gap-2 mb-5" data-id="element-223">
          <Clock className="w-5 h-5 md:w-6 md:h-6 text-brand-celesteDark shrink-0" data-id="element-224" />
          <h3 className="font-serif font-bold text-base md:text-xl text-brand-greenDark text-center" data-id="element-225">
            ⏰ PRECIO DE LANZAMIENTO — CIERRA EN:
          </h3>
        </div>

        <div className="flex items-center justify-center gap-2 md:gap-4 mb-5" data-id="element-226">
          <TimeBox value={timeLeft.h} label="Horas" data-id="element-227" />
          <span className="text-2xl md:text-4xl font-bold text-brand-celeste/60 pb-5" data-id="element-228">
            :
          </span>
          <TimeBox value={timeLeft.m} label="Min" data-id="element-229" />
          <span className="text-2xl md:text-4xl font-bold text-brand-celeste/60 pb-5" data-id="element-230">
            :
          </span>
          <TimeBox value={timeLeft.s} label="Seg" data-id="element-231" />
        </div>

        <div className="flex items-center justify-center gap-2 bg-amber-50 border border-amber-300/60 rounded-xl px-4 py-3 mb-6" data-id="element-232">
          <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-amber-600 shrink-0" data-id="element-233" />
          <p className="text-sm md:text-base font-semibold text-amber-900 text-center" data-id="element-234">
            Después sube $8.000 ARS.{' '}
            <span className="text-brand-green" data-id="element-235">No vuelve.</span>
          </p>
        </div>

        <div className="bg-white/80 rounded-2xl p-4 md:p-5 border border-brand-celeste/30" data-id="element-236">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-3" data-id="element-237">
            <div className="flex items-center gap-2" data-id="element-238">
              <span className="relative flex h-2.5 w-2.5" data-id="element-239">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-cupping opacity-75" data-id="element-240"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-cupping" data-id="element-241"></span>
              </span>
              <span className="font-semibold text-sm md:text-base text-brand-text" data-id="element-242">
                Cupos al precio de lanzamiento
              </span>
            </div>
            <span className="text-xs md:text-sm font-bold text-brand-green uppercase tracking-wide" data-id="element-243">
              Quedan pocos
            </span>
          </div>

          <div className="w-full bg-brand-celeste/15 rounded-full h-2.5 overflow-hidden mb-2" data-id="element-244">
            <div className="bg-gradient-to-r from-brand-celeste to-brand-celesteDark h-2.5 rounded-full transition-all duration-1000 ease-out" style={{
            width: `${spotsTaken}%`
          }} data-id="element-245" />
          </div>

          <p className="text-xs md:text-sm text-brand-text/70 text-center" data-id="element-246">
            <strong className="text-brand-green" data-id="element-247">{spotsTaken}%</strong> de los
            cupos al precio de lanzamiento ya tomados
          </p>
        </div>
      </div>
    </div>;
};
const protocols = ['01 · Cervicalgia y Contracturas por Tensión', '02 · Insomnio Reactivo', '03 · Ansiedad Crónica', '04 · Colon Irritable y Digestión Nerviosa', '05 · Dolor de Cabeza Tensional y Migraña', '06 · Fatiga Crónica y Falta de Energía', '07 · Dolor Lumbar', '08 · Depresión Leve y Bloqueo Emocional', '09 · Dolores Menstruales y Desregulación Hormonal', '10 · Hipertensión Emocional', '11 · Alergias y Defensas Bajas', '12 · Adicción al Azúcar y Compulsión Alimentaria'];
const protocolFeatures = ['✅ Objetivo clínico', '✅ Diagnóstico diferencial rápido', '✅ Algoritmo de 3 pasos', '✅ Receta de puntos', '✅ Versión de digitopuntura', '✅ Checklist clínico', '✅ Errores frecuentes', '✅ Resumen final'];
const bonuses = [{
  icon: Heart,
  image: 'https://cdn.magicpatterns.com/uploads/5Mcf1tDyDwEdFok1e3xusb/bonus1.png',
  imageAlt: 'Bonus 1 — Auxilio Inmediato · Guía Práctica de Puntos Vitales en MTC',
  title: 'BONUS 1 — AUXILIO INMEDIATO',
  pages: '30 páginas · listo para imprimir',
  color: 'text-brand-cupping',
  bgColor: 'bg-brand-cupping/10',
  borderColor: 'border-brand-cupping/30',
  desc: 'Protocolo completo para crisis nerviosas, ataques de pánico e hiperventilación en sesión.',
  result: '✅ Respuesta clara ante cualquier urgencia clínica',
  price: '$12.000'
}, {
  icon: Leaf,
  image: 'https://cdn.magicpatterns.com/uploads/4gRSKiiHBMFXAraSLczCx2/bonus2.png',
  imageAlt: 'Bonus 2 — Hierbas Argentinas',
  title: 'BONUS 2 — HIERBAS Y RECURSOS EN ARGENTINA',
  pages: '30 páginas · listo para imprimir',
  color: 'text-brand-green',
  bgColor: 'bg-brand-green/10',
  borderColor: 'border-brand-green/30',
  desc: 'Directorio de hierbas medicinales disponibles en el mercado argentino. Dónde conseguirlas y cómo recomendarlas.',
  result: '✅ Recomendá con criterio, no a ciegas',
  price: '$10.000'
}, {
  icon: Apple,
  image: 'https://cdn.magicpatterns.com/uploads/vwdvW81esa5ovftCpag1qo/bonus3.png',
  imageAlt: 'Bonus 3 — Guía de Alimentación por Cuadro Clínico',
  title: 'BONUS 3 — ALIMENTOS POR CUADRO CLÍNICO',
  pages: '30 páginas · listo para imprimir',
  color: 'text-brand-celesteDark',
  bgColor: 'bg-brand-celeste/10',
  borderColor: 'border-brand-celeste/30',
  desc: 'Qué consumir y qué evitar para cada uno de los 12 cuadros. Un plan concreto para reforzar el tratamiento entre sesiones.',
  result: '✅ El paciente colabora con su propio proceso',
  price: '$8.000'
}, {
  icon: ClipboardList,
  image: 'https://cdn.magicpatterns.com/uploads/5QFVLxjAthk4H9M1mkh7s9/bonus4.png',
  imageAlt: 'Bonus 4 — Ficha del Paciente · Herramienta clínica de MTC',
  title: 'BONUS 4 — FICHA DEL PACIENTE',
  pages: '4 páginas imprimibles · 2 versiones',
  color: 'text-brand-greenDark',
  bgColor: 'bg-brand-greenLight/20',
  borderColor: 'border-brand-green/30',
  desc: 'Plantillas imprimibles de diagnóstico y seguimiento. Registrá en una sola hoja la información más importante de cada consulta.',
  result: '✅ Continuidad clínica desde el primer paciente',
  price: '$6.000'
}];
const planComparison = [{
  situation: 'El paciente no entiende su diagnóstico de MTC',
  without: 'Explicás y no convencés',
  with: 'Guión claro listo. 2 minutos.'
}, {
  situation: 'La sesión termina y el paciente no reserva el próximo turno',
  without: 'Se va sin compromiso',
  with: 'Protocolo de Cierre aplicado. Turno reservado.'
}, {
  situation: 'Querés armar un plan de 8 sesiones',
  without: 'Improvisás sesión a sesión',
  with: 'Plan estructurado listo para presentar'
}, {
  situation: 'Necesitás recuperar un paciente inactivo',
  without: 'No sabés qué escribirle',
  with: 'Guión de WhatsApp listo para enviar'
}, {
  situation: 'Querés crecer en redes sociales',
  without: 'No sabés qué publicar',
  with: 'Estrategia de contenido para Instagram'
}, {
  situation: 'Querés subir tus precios',
  without: 'Miedo a perder pacientes',
  with: 'Sistema de valor percibido paso a paso'
}];
const maestriaFeatures = ['Protocolo de Cierre de Sesión', 'Guiones de WhatsApp listos', 'Sistema de planes de 4, 8 y 12 sesiones', 'Cómo explicar la MTC a pacientes escépticos', 'Estrategia de contenido para Instagram', 'Alianzas con otros profesionales de salud', 'Cómo subir precios sin perder pacientes'];
const plan1Items = [{
  name: 'Tomo Principal — Guía Maestra de Protocolos',
  value: '$40.000 ARS'
}, {
  name: 'Bonus 1: Auxilio Inmediato',
  value: '$12.000 ARS'
}, {
  name: 'Bonus 2: Hierbas y Recursos en Argentina',
  value: '$10.000 ARS'
}, {
  name: 'Bonus 3: Alimentos por Cuadro Clínico',
  value: '$8.000 ARS'
}, {
  name: 'Bonus 4: Ficha del Paciente',
  value: '$6.000 ARS'
}];
const plan2Items = [...plan1Items, {
  name: 'Maestría en Consulta (45 pág)',
  value: '$23.000 ARS'
}];
const ValueTable = ({
  items,
  total,
  price
}: {
  items: {
    name: string;
    value: string;
  }[];
  total: string;
  price: string;
}) => <div className="rounded-2xl overflow-hidden border border-brand-celeste/30 bg-white/80 mb-6" data-id="element-248">
    <div className="p-4 md:p-5 space-y-3" data-id="element-249">
      {items.map((item, i) => <div key={i} className="flex justify-between items-center gap-4 border-b border-brand-celeste/15 pb-3 last:border-0 last:pb-0" data-id="element-250">
          <span className="text-brand-text/90 font-medium text-xs md:text-sm" data-id="element-251">
            {item.name}
          </span>
          <span className="text-brand-text/60 line-through text-xs md:text-sm whitespace-nowrap" data-id="element-252">
            {item.value}
          </span>
        </div>)}
    </div>
    <div className="bg-brand-surfaceLight/80 p-4 md:p-5 border-t border-brand-celeste/30 space-y-2" data-id="element-253">
      <div className="flex justify-between items-center gap-4" data-id="element-254">
        <span className="font-serif font-bold text-sm md:text-base text-brand-text" data-id="element-255">
          Valor total
        </span>
        <span className="font-serif font-bold text-sm md:text-base text-brand-text/60 line-through whitespace-nowrap" data-id="element-256">
          {total}
        </span>
      </div>
      <div className="flex justify-between items-center gap-4" data-id="element-257">
        <span className="font-serif font-bold text-base md:text-lg text-brand-green" data-id="element-258">
          Tu inversión hoy
        </span>
        <span className="font-serif font-bold text-xl md:text-2xl text-brand-green whitespace-nowrap" data-id="element-259">
          {price}
        </span>
      </div>
    </div>
  </div>;
export const MethodOverview = () => {
  return <Section id="method" className="relative" data-id="element-260">
      {/* Decorative TCM elements */}
      <AcupunctureNeedle className="hidden lg:block absolute top-20 right-[3%] w-5 h-24 opacity-25 animate-float-soft" color="#B91C1C" style={{
      transform: 'rotate(-15deg)'
    }} data-id="element-261" />
      <MoxaStick className="hidden lg:block absolute top-60 left-[2%] w-7 h-28 opacity-30 animate-float-soft" style={{
      transform: 'rotate(12deg)',
      animationDelay: '1.5s'
    }} data-id="element-262" />

      <div className="text-center mb-12" data-id="element-263">
        <div className="inline-flex items-center space-x-2 bg-white border border-brand-celeste/40 px-4 py-1.5 rounded-full mb-6 shadow-sm" data-id="element-264">
          <span className="text-xl" data-id="element-265">🏆</span>
          <span className="font-serif font-semibold tracking-wider text-brand-green text-sm md:text-base" data-id="element-266">
            KIT MEDICINA TRADICIONAL CHINA™
          </span>
        </div>
        <h2 className="text-2xl md:text-5xl font-display font-bold mb-4 leading-tight text-brand-greenDark" data-id="element-267">
          1 Tomo Principal + 4 Bonus
        </h2>
        <p className="text-brand-text/70 text-base md:text-lg" data-id="element-268">
          154 páginas — Todo resuelto.
        </p>
      </div>

      {/* Tomo Principal block */}
      <div className="max-w-4xl mx-auto mb-12" data-id="element-269">
        <div className="rounded-2xl p-6 md:p-8 border bg-brand-green/5 border-brand-green/25 backdrop-blur-sm shadow-sm mb-6" data-id="element-270">
          <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6" data-id="element-271">
            <div className="shrink-0 mx-auto md:mx-0 w-24 md:w-32" data-id="element-272">
              <img src="https://cdn.magicpatterns.com/uploads/kjf9D7UUPLZrS4RVWyWtTf/tomo1.png" alt="Tomo Principal — La Guía Maestra de Protocolos" className="w-full h-auto rounded-md shadow-lg ring-1 ring-brand-green/15" draggable={false} data-id="element-273" />
            </div>
            <div className="flex-grow" data-id="element-274">
              <h4 className="font-serif font-bold mb-2 text-lg text-brand-greenDark" data-id="element-275">
                TOMO PRINCIPAL — LA GUÍA MAESTRA DE PROTOCOLOS
              </h4>
              <p className="text-brand-text/90 font-medium mb-2 text-base" data-id="element-276">
                12 Soluciones Prácticas para el Consultorio Moderno · 60 páginas
              </p>
              <p className="text-brand-text/80 text-sm mb-4" data-id="element-277">
                El manual central de tu práctica clínica. 12 protocolos
                completos, cada uno con algoritmo de 3 pasos para que entres a
                cada sesión con dirección absoluta.
              </p>

              {/* Protocols list */}
              <div className="bg-white/80 p-4 rounded-lg border border-brand-green/15 mb-4" data-id="element-278">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-green mb-3" data-id="element-279">
                  12 Protocolos incluidos
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-xs md:text-sm text-brand-text/85" data-id="element-280">
                  {protocols.map((p, i) => <div key={i} className="flex items-start" data-id="element-281">
                      <span className="text-brand-celesteDark mr-2 shrink-0" data-id="element-282">
                        ·
                      </span>
                      <span data-id="element-283">{p.replace(/^\d+\s·\s/, '')}</span>
                    </div>)}
                </div>
              </div>

              {/* Per-protocol features */}
              <div className="bg-white/80 p-4 rounded-lg border border-brand-green/15" data-id="element-284">
                <p className="text-xs font-bold uppercase tracking-wider text-brand-green mb-3" data-id="element-285">
                  Cada protocolo incluye
                </p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs md:text-sm text-brand-text/85" data-id="element-286">
                  {protocolFeatures.map((f, i) => <div key={i} className="flex items-center" data-id="element-287">
                      <span data-id="element-288">{f}</span>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mb-8" data-id="element-289">
          <h3 className="text-xl md:text-2xl font-serif font-bold text-brand-greenDark" data-id="element-290">
            🎁 4 BONUS ESTRATÉGICOS
          </h3>
          <p className="text-xs md:text-sm uppercase tracking-wider text-brand-celesteDark mt-1 font-bold" data-id="element-291">
            SIEMPRE INCLUIDOS
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-292">
          {bonuses.map((bonus, i) => {
          const Icon = bonus.icon;
          const numberLabel = String(i + 1).padStart(2, '0');
          return <div key={i} className="glass-panel rounded-2xl p-5 border-brand-celeste/30 relative overflow-hidden bg-white/90 flex flex-col" data-id="element-293">
                <div className={`absolute top-3 left-3 ${bonus.bgColor} border ${bonus.borderColor} ${bonus.color} text-[11px] font-mono font-bold px-2 py-0.5 rounded-md`} data-id="element-294">
                  {numberLabel}
                </div>
                <div className="absolute top-0 right-0 bg-brand-celesteDark text-white text-xs font-bold px-3 py-1 rounded-bl-lg" data-id="element-295">
                  GRATIS
                </div>
                {bonus.image ? <img src={bonus.image} alt={bonus.imageAlt} draggable={false} className="w-20 md:w-24 h-auto rounded-md shadow-md ring-1 ring-black/5 mb-3 mt-6" data-id="element-296" /> : <Icon className={`w-7 h-7 ${bonus.color} mb-3 mt-6`} data-id="element-297" />}
                <h4 className="text-base md:text-lg font-serif font-bold mb-2 pr-14 leading-tight text-brand-text" data-id="element-298">
                  {bonus.title}
                </h4>
                <p className={`${bonus.color} text-xs font-medium mb-3`} data-id="element-299">
                  {bonus.pages}
                </p>
                <p className="text-brand-text/80 mb-3 text-xs md:text-sm flex-grow" data-id="element-300">
                  {bonus.desc}
                </p>
                <div className="text-xs font-medium text-brand-green mb-2" data-id="element-301">
                  {bonus.result}
                </div>
                <div className="text-xs text-brand-text/50 mt-auto" data-id="element-302">
                  <span className="line-through" data-id="element-303">{bonus.price}</span>
                  <span className="ml-2 font-bold text-brand-celesteDark" data-id="element-304">
                    → GRATIS
                  </span>
                </div>
              </div>;
        })}
        </div>

        <div className="text-center mt-8" data-id="element-305">
          <div className="inline-block glass-panel px-6 py-3 rounded-full border-brand-celeste/30 bg-white/80" data-id="element-306">
            <p className="text-sm md:text-base font-medium text-brand-text/80" data-id="element-307">
              Valor total bonos{' '}
              <span className="line-through text-brand-text/50" data-id="element-308">$36.000</span>{' '}
              <span className="text-brand-green font-bold" data-id="element-309">
                → GRATIS con tu Kit MTC
              </span>
            </p>
          </div>
        </div>
      </div>

      <LaunchUrgency data-id="element-310" />

      <Pricing data-id="element-311" />

      {/* Maestría en Consulta — comparison */}
      <div className="max-w-4xl mx-auto mb-12 mt-12" data-id="element-312">
        <div className="bg-white rounded-2xl p-5 md:p-6 border border-brand-celeste/30 shadow-sm" data-id="element-313">
          <p className="text-base md:text-lg font-semibold text-brand-greenDark mb-4 text-center" data-id="element-314">
            🎓 ¿Qué agrega la Maestría en Consulta?
          </p>

          <div className="hidden md:block" data-id="element-315">
            <div className="grid grid-cols-3 gap-2 text-[11px] font-semibold uppercase tracking-wide text-brand-text/60 border-b border-brand-celeste/20 pb-2 mb-2" data-id="element-316">
              <div data-id="element-317">Situación</div>
              <div className="text-brand-cupping" data-id="element-318">❌ Sin Maestría</div>
              <div className="text-brand-green" data-id="element-319">✅ Con Maestría</div>
            </div>
            <div className="space-y-2" data-id="element-320">
              {planComparison.map((row, i) => <div key={i} className="grid grid-cols-3 gap-2 text-xs text-brand-text/80 py-1.5 border-b border-brand-celeste/10 last:border-0" data-id="element-321">
                  <div className="font-medium" data-id="element-322">{row.situation}</div>
                  <div className="text-brand-cupping/80" data-id="element-323">{row.without}</div>
                  <div className="text-brand-green font-medium" data-id="element-324">{row.with}</div>
                </div>)}
            </div>
          </div>

          <div className="md:hidden space-y-3" data-id="element-325">
            {planComparison.map((row, i) => <div key={i} className="border-b border-brand-celeste/15 pb-3 last:border-0 last:pb-0" data-id="element-326">
                <p className="text-xs font-semibold text-brand-text mb-1.5" data-id="element-327">
                  {row.situation}
                </p>
                <div className="text-[11px] text-brand-cupping/80 mb-0.5" data-id="element-328">
                  ❌ {row.without}
                </div>
                <div className="text-[11px] text-brand-green font-medium" data-id="element-329">
                  ✅ {row.with}
                </div>
              </div>)}
          </div>

          <p className="text-sm font-medium text-brand-celesteDark italic text-center mt-5" data-id="element-330">
            Un solo paciente recuperado con esta guía ya paga la diferencia de
            precio.
          </p>
        </div>
      </div>

      {/* Plans */}
      <div className="text-center mb-8" data-id="element-331"></div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 max-w-6xl mx-auto" data-id="element-332">
        {/* Plan 1 */}

        {/* Plan 2 — featured */}
      </div>
    </Section>;
};